// The uploaded icons' render assets a page shows, out of CustomizerScreen and
// ProcessOrderScreen (backfill PR 31, where each held a copy): the cache by asset id of what
// POST /api/user-icons/render-assets sends (a PNG's signed link with its expiry and pixel
// size, an SVG's markup), filled for the ids the page's designs show and not yet cached,
// refreshed for the PNG links about to expire when the tab becomes visible again, and
// refreshed once more for an icon whose image failed to load, three times at most per icon
// and once at a time. A failed request toasts getError's message, which every axios error
// has (the fallbacks the screens held for an error without one never showed: deleted in PR
// 31, user decision).
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../api';
import { toastRequestError } from '../utils';

export const SIGNED_URL_REFRESH_BUFFER_MS = 60 * 60 * 1000; // refresh signed URLs 1 hour before expiry

const MAX_USER_ICON_ASSET_REFRESH_ATTEMPTS = 3;

export function useUserIconAssets({
  usedUserIconAssetIds, // the uploaded icons the page's designs show, each once as a string (collectUserIconAssetIds)
  signedIn, // the customizer passes its stored login, which gates every request and is a dependency of the effects below; the order processing, an admin page, passes true
  t,
}) {
  const [userIconAssetsById, setUserIconAssetsById] = useState({});

  const cachedUserIconAssetIdsKey = useMemo(
    () => Object.keys(userIconAssetsById).sort().join('|'),
    [userIconAssetsById]
  ); // creates a stable dependency key for cached uploaded icon assets

  const usedUserIconAssetIdsKey = useMemo(
    () => [...usedUserIconAssetIds].sort().join('|'),
    [usedUserIconAssetIds]
  ); // creates a stable dependency key for uploaded icon ids used in the design

  const fetchAndMergeUserIconAssets = useCallback(
    async (assetIds, shouldCancel = () => false) => {
      if (assetIds.length === 0) {
        return []; // nothing to fetch
      }

      const { data } = await api.post('/api/user-icons/render-assets', {
        assetIds,
      }); // the ids come from collectUserIconAssetIds, each once as a string

      if (shouldCancel()) {
        return []; // ignore the response if the caller no longer needs these assets
      }

      setUserIconAssetsById((prev) => ({
        ...prev,
        ...Object.fromEntries(
          data.map((asset) => [String(asset.assetId), asset])
        ),
      }));

      return data;
    },
    []
  ); // fetch uploaded icon render assets and merge them into the frontend memory cache (the order processing reads the answer to check a design's assets before its printing files; the customizer's copy returned nothing)

  const refreshingUserIconAssetIdsRef = useRef(new Set()); // tracks uploaded icon assets that already have a refresh request in progress
  const userIconAssetRefreshAttemptsRef = useRef(new Map()); // tracks failed-load refresh attempts per uploaded icon asset

  const refreshUserIconAsset = useCallback(
    async (assetId) => {
      if (!signedIn || !assetId) {
        return; // do not refresh uploaded icon assets if no user is signed in or no assetId was provided
      }

      const assetIdString = String(assetId);

      const attempts =
        userIconAssetRefreshAttemptsRef.current.get(assetIdString) || 0;

      if (attempts >= MAX_USER_ICON_ASSET_REFRESH_ATTEMPTS) {
        return; // stop retrying this asset after repeated image load failures
      }

      if (refreshingUserIconAssetIdsRef.current.has(assetIdString)) {
        return; // avoid repeated refreshes for the same asset while a refresh is already in progress
      }

      userIconAssetRefreshAttemptsRef.current.set(assetIdString, attempts + 1); // count this onError-triggered refresh attempt, even if the API request itself succeeds

      refreshingUserIconAssetIdsRef.current.add(assetIdString);

      try {
        await fetchAndMergeUserIconAssets([assetIdString]);
      } catch (err) {
        toastRequestError(err, t);
      } finally {
        refreshingUserIconAssetIdsRef.current.delete(assetIdString);
      }
    },
    [signedIn, fetchAndMergeUserIconAssets]
  ); // refreshes one uploaded icon render asset

  useEffect(() => {
    userIconAssetRefreshAttemptsRef.current.clear();
  }, [usedUserIconAssetIdsKey]); // allow fresh retry attempts when the design starts using a different set of uploaded icons

  useEffect(() => {
    if (!signedIn || usedUserIconAssetIds.length === 0) {
      return;
    }

    const missingAssetIds = usedUserIconAssetIds.filter(
      (assetId) => !userIconAssetsById[assetId]
    ); // only fetch uploaded icon assets that are used in the design and not already cached in frontend memory

    if (missingAssetIds.length === 0) {
      return;
    }

    let cancelled = false; // prevents state updates if the component unmounts or the effect reruns before the request finishes

    const fetchMissingAssets = async () => {
      try {
        await fetchAndMergeUserIconAssets(missingAssetIds, () => cancelled); // fetch missing render assets and skip merging if the effect has been cancelled
      } catch (err) {
        if (!cancelled) {
          toastRequestError(err, t);
        }
      }
    };

    fetchMissingAssets();

    return () => {
      cancelled = true;
    };
  }, [
    signedIn,
    usedUserIconAssetIdsKey,
    cachedUserIconAssetIdsKey,
    fetchAndMergeUserIconAssets,
  ]); // loads missing uploaded icon render assets for the current design

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!signedIn) {
        return; // don't fetch user icon assets if no user is logged in
      }

      if (document.visibilityState !== 'visible') {
        return; // only check signed URLs when the tab becomes visible again
      }

      const expiringPngAssetIds = usedUserIconAssetIds.filter((assetId) => {
        const asset = userIconAssetsById[assetId]; // get the cached render asset for the current assetId

        if (!asset || asset.format !== 'png') {
          return false; // only PNG assets use signed URLs that may expire
        }

        if (!asset.iconUrlExpiresAt) {
          return true; // if no expiry info exists, refresh defensively
        }

        return (
          Date.now() >
          Number(asset.iconUrlExpiresAt) - SIGNED_URL_REFRESH_BUFFER_MS
        ); // refresh the signed URL if it is close to expiring
      });

      if (expiringPngAssetIds.length > 0) {
        fetchAndMergeUserIconAssets(expiringPngAssetIds).catch((err) => {
          toastRequestError(err, t);
        }); // fetch fresh signed URLs for PNG assets that are close to expiring
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange); // run the check when the user returns to the tab

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange); // cleanup the event listener when dependencies change or the component unmounts
    };
  }, [
    usedUserIconAssetIdsKey,
    userIconAssetsById, // the cache itself, not its key set: a refresh replaces an asset under its key, and the handler has to see the new expiry (until PR 31 it kept the old cache and refreshed the same link on every return to the tab)
    fetchAndMergeUserIconAssets,
    signedIn,
  ]); // refreshes uploaded PNG signed URLs when the tab becomes visible again and a cached URL is close to expiring

  return {
    userIconAssetsById,
    setUserIconAssetsById,
    fetchAndMergeUserIconAssets,
    refreshUserIconAsset,
  };
} // the cache of uploaded icon render assets and its refreshes
